import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Edit2, ChevronRight, ChevronDown, Package } from 'lucide-react';
import { Recipe } from '@/shared/model';
import { RecipeCategoryMap } from '@/stores';

interface RecipesTableProps {
    recipes: RecipeCategoryMap;
    onEdit: (recipe: Recipe, category: string) => void;
    onDelete: (id: string, category: string) => void;
}

export const RecipesTable: FC<RecipesTableProps> = ({ 
    recipes, 
    onEdit, 
    onDelete 
}) => {
    const { t } = useTranslation();
    const [expandedCats, setExpandedCats] = useState<string[]>(['wall', 'floor', 'general']);

    const toggleCat = (cat: string) => {
        setExpandedCats(prev => 
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };

    const categories = Object.keys(recipes);

    return (
        <div className="w-full">
            {categories.length === 0 && (
                <div className="p-12 text-center text-slate-400 dark:text-slate-600">
                    {t('recipes.empty_list', 'Шаблонов пока нет')}
                </div>
            )}
            
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {categories.map(cat => {
                    const catRecipes = recipes[cat] || [];
                    const isExpanded = expandedCats.includes(cat);
                    
                    if (catRecipes.length === 0 && !['wall', 'floor', 'general'].includes(cat)) return null;

                    return (
                        <div key={cat} className="flex flex-col">
                            {/* Category Header */}
                            <button 
                                onClick={() => toggleCat(cat)}
                                className="flex items-center gap-3 px-6 py-4 bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors text-left group"
                            >
                                {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                                <div className="flex-1 flex items-center gap-2">
                                    <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded">
                                        {t(`catalog.types.${cat}`, cat.toUpperCase())}
                                    </span>
                                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                                        (<span className="font-mono tabular-nums">{catRecipes.length}</span>)
                                    </span>
                                </div>
                            </button>

                            {/* Recipes List */}
                            {isExpanded && (
                                <div className="divide-y divide-slate-50 dark:divide-slate-800/50 bg-white dark:bg-slate-900">
                                    {catRecipes.length === 0 ? (
                                        <div className="px-12 py-6 text-xs text-slate-400 dark:text-slate-600 italic">
                                            {t('recipes.empty_cat', 'В этой категории пока нет шаблонов')}
                                        </div>
                                    ) : (
                                        catRecipes.map(recipe => (
                                            <div key={recipe.id} className="flex items-center justify-between px-6 py-4 hover:bg-blue-50/20 dark:hover:bg-blue-900/5 transition-all group">
                                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                                    <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0 shadow-sm border border-purple-100 dark:border-purple-800/50">
                                                        <Package size={20} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-bold text-slate-700 dark:text-slate-200 truncate">
                                                            {recipe.nameKey ? t(recipe.nameKey, recipe.name) : recipe.name}
                                                        </h4>
                                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium uppercase tracking-tight">
                                                            {t('recipes.services_count', '{{count}} услуг', { count: recipe.layers.length })}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <button 
                                                        onClick={() => onEdit(recipe, cat)}
                                                        className="p-2.5 text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl transition-all active:scale-95"
                                                        title={t('common.edit', 'Редактировать')}
                                                    >
                                                        <Edit2 size={18}/>
                                                    </button>
                                                    <button 
                                                        onClick={() => onDelete(recipe.id, cat)}
                                                        className="p-2.5 text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-all active:scale-95"
                                                        title={t('common.delete', 'Удалить')}
                                                    >
                                                        <Trash2 size={18}/>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
